/**
 * The room before the first deal.
 *
 * Everybody in the lobby sees the same thing — the code, the QR, who has sat
 * down — because anyone in the room may be the one holding the phone somebody
 * else is squinting at. The host additionally gets the settings and the button
 * that ends the wait.
 *
 * Nothing here deals. The first round waits for the host, always: a room that
 * started itself the moment the last guest arrived gave nobody a chance to
 * agree on anything, and held no seat for a friend still typing the code.
 *
 * ## Two screens, not one scroll (F1, F8)
 *
 * This carried everything at once: the code, the QR, four share buttons, the
 * seat list, the watchers, the turn timer, keep-dealing, the game picker, the
 * house rules and three toggles. On a phone that is a long scroll with no
 * hierarchy, and the thing a host actually needs first - the code, to read out
 * loud - was above a wall of settings nobody had asked about yet.
 *
 * So: **Invite** first, **Configure** behind it.
 *
 * Invite comes first because the long pole is people arriving, not the host
 * choosing a turn timer. Configuring takes thirty seconds; getting four people
 * to a table takes minutes. Start the slow thing first and do the fast thing
 * underneath it, and by the time the rules are settled people are already
 * sitting down. It is what you do in a room: call people over, then argue about
 * the rules while they pull up chairs.
 *
 * **Tabs, not a wizard.** People arrive late and the code needs re-reading, so
 * going back to Invite has to be free rather than a step backwards.
 *
 * **Start lives on both.** The room is live from the moment it exists, so what
 * gates the deal is whether enough people are here - never which tab the host
 * happens to be looking at.
 */

import { useEffect, useState } from 'react';
import type { MatchView } from '../net/match.js';
import { isSolo } from '../net/match.js';
import { QrCode } from '../render/QrCode.js';
import { useSession } from '../session-context.js';
import { useCatalog } from '../use-catalog.js';
import { avatarEmoji } from '../render/avatars.js';
import { Icon } from '../render/Icon.js';
import { gameArt } from '../render/game-art.js';
import { VariantControls } from './VariantControls.js';
import { GamePicker } from './GamePicker.js';
import { TurnTimer } from './TurnTimer.js';
import { SettingRow } from './SettingRow.js';
import { SoundSetting } from './SoundSetting.js';
import { ActionBar, BackButton } from './ActionBar.js';
import type { VariantSpecWire } from '../net/session.js';

function joinUrl(code: string): string {
  try {
    return new URL(`/join/${code}`, window.location.origin).toString();
  } catch {
    return `/join/${code}`;
  }
}

/**
 * Getting the link to somebody who is not in the room.
 *
 * Three ways, because the right one depends on where the person is: the code
 * for somebody across the table, the QR for somebody across the room, and a
 * link for somebody across the country. The system share sheet is offered when
 * the browser has one, and `mailto:`/`sms:` when it does not — both are plain
 * links, so nothing here needs a permission or a server.
 */
function Invite({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const url = joinUrl(code);
  const line = `Join my card game — ${url} (room code ${code})`;

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  /*
   * Tiles rather than four full-width buttons.
   *
   * Stacked, these were four bars the size of the primary action, so the way
   * to send somebody a link outweighed the code itself - and on a phone it
   * pushed everything else off the screen. As a row of tiles the whole set is
   * one band, which is also how the mockup draws it.
   */
  return (
    <div className="invite">
      <button
        type="button"
        className="invite-option"
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            } catch {
              // Clipboard access can be refused outright. The link is on screen
              // below regardless, which is the fallback that always works.
            }
          })();
        }}
      >
        <Icon name={copied ? 'info' : 'link'} />
        <span>{copied ? 'Copied' : 'Copy link'}</span>
      </button>

      {canShare ? (
        <button
          type="button"
          className="invite-option"
          onClick={() => {
            // A rejected share is somebody pressing cancel, not an error.
            void navigator.share({ title: 'Revelry', text: line, url }).catch(() => undefined);
          }}
        >
          <Icon name="share" />
          <span>Share</span>
        </button>
      ) : null}

      <a
        className="invite-option"
        href={`mailto:?subject=Join my card game&body=${encodeURIComponent(line)}`}
      >
        <Icon name="mail" />
        <span>Email</span>
      </a>
      <a className="invite-option" href={`sms:?&body=${encodeURIComponent(line)}`}>
        <Icon name="message" />
        <span>Text</span>
      </a>
    </div>
  );
}

export function Lobby({
  view,
  roomCode,
  gameName,
  variantSpecs,
  turnLimits,
  onHostAction,
  onLeave,
}: {
  view: MatchView;
  roomCode: string;
  gameName: string;
  /** What this game offers. Declared by the game, rendered generically. */
  variantSpecs: VariantSpecWire[];
  /** Bounds the server will accept, so no offered choice can be refused. */
  turnLimits?: { min: number; default: number; max: number } | undefined;
  onHostAction: (action: Record<string, unknown>) => void;
  /** Walking away before the first deal. The room keeps going without you. */
  onLeave: () => void;
}) {
  /*
   * Nobody picks a number of seats any more - seats follow players. So the
   * question is not "is the table full" but "are the people here a legal table
   * for this game".
   */
  const seated = view.seats.length;
  const needed = Math.max(0, view.seatRange.min - seated);
  const tooMany = seated > view.seatRange.max;
  /* Read off the seat range, the same way the table does - a patience is a
     game that seats one, not a game with a flag saying so. */
  const solo = isSolo(view);
  const ready = needed === 0 && !tooMany;

  /*
   * Invite first, and it stays first on every visit.
   *
   * Not remembered between renders of the room on purpose: a host who wandered
   * to Configure and came back later is almost always coming back to read the
   * code to somebody new.
   */
  const [tab, setTab] = useState<'invite' | 'configure'>('invite');

  /*
   * Why everybody is suddenly sitting in a lobby (L?).
   *
   * The host stopping a game moves every screen in the room back here, and
   * without a word about it the table simply vanishes mid-hand - which reads
   * as the app losing its place rather than as somebody's decision. A player
   * who is told can shrug; a player who is not asks what happened, and the
   * only person who can answer is the one who did it.
   *
   * Not shown to the host: they are the one who pressed it, and a room
   * announcing your own decision back to you is the app explaining you to
   * yourself. A stand-in host who ended it sees nothing for the same reason.
   *
   * It goes after a few seconds rather than staying. The fact stays on the
   * server until the next deal so that a client arriving late or reconnecting
   * still gets told, but on screen it is news, and news stops being news.
   */
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    if (!view.endedByHost || view.isHost) {
      setEnded(false);
      return undefined;
    }
    setEnded(true);
    const timer = window.setTimeout(() => setEnded(false), 6000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [view.endedByHost, view.isHost]);

  /*
   * The catalogue entry behind `gameName`.
   *
   * Optional on purpose: the catalogue is fetched per session and a guest who
   * came straight in on a code may not have it yet, so everything it feeds is
   * guarded and the screen is complete without it.
   */
  const { catalog } = useCatalog();
  const game = catalog?.games.find((candidate) => candidate.id === view.gameId);
  const art = game ? gameArt(game.id, game.family) : null;

  // Whether voice can work on this server at all. See the chip below.
  // Both have to hold: the server has to be able to carry voice, and the
  // host has to allow it here.
  const voiceHere = useSession().config?.voice?.available === true && view.allowVoice;

  /*
   * One button, at the foot of the screen, on every tab.
   *
   * What gates the deal is whether enough people are here, never which tab the
   * host is looking at - and a host who has just finished setting house rules
   * should not have to navigate back to Invite to begin. It used to be written
   * into each panel to achieve that, which is the same control three times and
   * three places for it to fall out of step.
   */
  const startButton = view.isHost ? (
    <button
      type="button"
      className="cta cta-primary lobby-start"
      disabled={!ready}
      onClick={() => onHostAction({ action: 'START_ROUND' })}
    >
      <Icon name="spade" className="cta-mark" />
      <span className="cta-label">
        {ready ? 'Start the game' : tooMany ? 'Too many players' : `Need ${String(needed)} more`}
      </span>
      <Icon name="chevron-right" />
    </button>
  ) : null;

  return (
    <div className="lobby">
      {ended ? (
        <p className="banner warn" role="status">
          The host ended the game.
        </p>
      ) : null}

      {/*
        The game, not just its name (G2).

        The mockup heads this screen with the thing everybody is here for -
        mark, name, what it is, and the facts - and it is the same card people
        chose from in the library, which is the point: a guest who arrived on a
        code has never seen that page and this is the only place they are told
        what they have been invited to.

        The status line stays with it, because "3 more to start" is a fact
        about this table in the same way "4 players" is a fact about the game.
      */}
      <header className="lobby-head form-card">
        {art ? (
          <span className={`lobby-mark tone-${art.tone}`} aria-hidden="true">
            <Icon name={art.icon} />
          </span>
        ) : null}

        <div className="lobby-head-text">
          <h1 className="lobby-game">{gameName}</h1>
          {game?.blurb ? <p className="lobby-blurb">{game.blurb}</p> : null}
          {/*
            A patience has nobody to count and nobody to wait for.

            Every branch below is about the other people at the table - how
            many are here, how many more are needed, whether the host has
            dealt yet. On a one-seat game they all come out wrong, and the
            last one comes out absurd: "1 at the table — waiting for the host
            to deal" shown to the host, about themselves. The server deals a
            solo room the moment it can, so the only true thing to say here is
            that it is happening.
          */}
          <p className="lobby-status">
            {solo
              ? 'Dealing…'
              : tooMany
                ? `${String(seated)} at the table, and this game seats ${String(view.seatRange.max)}`
                : needed > 0
                  ? `${String(seated)} here · ${String(needed)} more to start`
                  : view.isHost
                    ? `${String(seated)} at the table. Deal when you are ready.`
                    : `${String(seated)} at the table — waiting for the host to deal.`}
          </p>
        </div>
      </header>

      {/* Only a host has a second tab to reach, so only a host gets a tab strip.
          For everybody else this screen IS the invite screen, and a lone tab
          would be a control that does nothing. A solo host has the same
          problem from the other side: there is nobody to invite, so the tab
          the strip switches AWAY from is the one that should not be there. */}
      {view.isHost && !solo ? (
        <div className="segmented lobby-tabs" role="tablist" aria-label="Lobby">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'invite'}
            className={`segment${tab === 'invite' ? ' chosen' : ''}`}
            onClick={() => setTab('invite')}
          >
            Invite
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'configure'}
            className={`segment${tab === 'configure' ? ' chosen' : ''}`}
            onClick={() => setTab('configure')}
          >
            Game options
          </button>
        </div>
      ) : null}

      {/*
        Nothing to invite anybody to.

        A one-seat game has no room code worth showing, no QR worth scanning,
        no link worth copying, no "can we talk?" to answer and no list of
        other people to read - and it had all five. What is left is the way
        in, which the server almost always beats you to.
      */}
      {/* A non-host has no tabs, so `tab` must not gate what they see. */}
      {!solo && (!view.isHost || tab === 'invite') ? (
        <>
          <div className="lobby-code">
            <div className="room">
              <span className="room-label">Room code</span>
              <span className="room-code">{roomCode}</span>
            </div>
            <QrCode text={joinUrl(roomCode)} size={124} />
          </div>

          <Invite code={roomCode} />

          {/*
            What this table has on (G5).

            On Invite rather than Game options, because it answers a question
            the people ARRIVING have - "can we talk?" - and they are the ones
            who cannot see the settings. The host sees it too, which is the
            cheapest possible confirmation that the toggle they just moved did
            something.

            Voice is two separate facts and both have to hold. The host may
            switch it off for this room (L3), and the deployment may not carry
            it at all - no STUN, nobody can talk whoever hosts. Reported as one
            line because to a player arriving they are the same question, and
            the answer to "can we talk?" is no if either is false.
          */}
          <p className="room-features">
            <span className={view.allowTextChat ? 'feature on' : 'feature'}>
              <Icon name="message" />
              {view.allowTextChat ? 'Typing on' : 'Quick phrases only'}
            </span>
            <span className={voiceHere ? 'feature on' : 'feature'}>
              <Icon name="mic" />
              {voiceHere ? 'Voice available' : 'No voice here'}
            </span>
          </p>

          <div className="lobby-people form-card">
          <ul className="lobby-seats">
            {view.seats.map((seat) => (
              <li key={seat.seat} className={seat.connected ? 'here' : 'here offline'}>
                {/* The face people picked, beside the name they picked. A list of
                    bare names makes "who is already here" a reading task. */}
                <span className="lobby-seat-face" aria-hidden="true">
                  {avatarEmoji(seat.avatarId)}
                </span>
                <span className="lobby-seat-name">{seat.displayName}</span>
                {seat.isHost ? (
                        <span className="host-tag">
                          {/* A crown and the word, not the crown alone: a glyph with no
                              text is a thing to decode, and "who is running this" is
                              exactly what a new arrival is trying to find out. */}
                          <Icon name="crown" /> host
                        </span>
                ) : null}
                {!seat.connected ? <span className="seat-state">offline</span> : null}
                {/* The host decides who plays. Not offered on their own row: the
                    host always plays, so the server would refuse it. */}
                {view.isHost && !seat.isHost ? (
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onHostAction({ action: 'MAKE_OBSERVER', seat: seat.seat })}
                  >
                    To watching
                  </button>
                ) : null}
              </li>
            ))}
            {Array.from({ length: needed }, (_, index) => (
              <li key={`empty-${String(index)}`} className="empty">
                {/* A "+" where a face goes, so an empty chair lines up with
                    the taken ones instead of being a shorter row of grey. */}
                <span className="lobby-seat-face" aria-hidden="true">
                  +
                </span>
                <span className="lobby-seat-name">Waiting for someone</span>
              </li>
            ))}
          </ul>

          {view.observers.length > 0 ? (
            <div className="lobby-watching">
              <span className="field-label">
                Watching · {view.observers.length}
              </span>
              <ul className="lobby-seats">
                {view.observers.map((observer) => (
                  <li key={observer.id} className="watching">
                    <span className="lobby-seat-face" aria-hidden="true">
                      {avatarEmoji(observer.avatarId)}
                    </span>
                    <span className="lobby-seat-name">{observer.displayName}</span>
                    {view.isHost ? (
                      <button
                        type="button"
                        className="text-button"
                        disabled={seated >= view.seatRange.max}
                        onClick={() =>
                          onHostAction({ action: 'MAKE_PLAYER', observerId: observer.id })
                        }
                      >
                        Add to game
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          </div>

        </>
      ) : null}

      {view.isHost && tab === 'configure' ? (
        <div className="lobby-settings form-card">
          <TurnTimer
            limits={turnLimits}
            current={view.turnLimitMs}
            onChoose={(turnLimitMs) => onHostAction({ action: 'SET_TURN_LIMIT', turnLimitMs })}
          />

          <SettingRow
            icon="repeat"
            title="Keep dealing"
            help="Deal the next game automatically when this one ends"
            checked={view.autoContinue}
            onChange={(next) => onHostAction({ action: 'SET_AUTO_CONTINUE', enabled: next })}
          />

          {/* The room keeps its people when the game changes, so this belongs
              here as much as it does after a game has finished. */}
          <GamePicker view={view} onHostAction={onHostAction} />

          <VariantControls specs={variantSpecs} view={view} onHostAction={onHostAction} />

          <SettingRow
            icon="eye"
            title="Let people watch"
            help="Off means players only"
            checked={view.allowObservers}
            onChange={(next) => onHostAction({ action: 'SET_ALLOW_OBSERVERS', enabled: next })}
          />

          {/*
            Typing only. The quick phrases are never switchable, and the log
            keeps working either way — which is why the help text says what is
            left rather than what is lost.
          */}
          <SettingRow
            icon="message"
            title="Let people type"
            help="Off leaves the quick phrases"
            checked={view.allowTextChat}
            onChange={(next) => onHostAction({ action: 'SET_ALLOW_TEXT_CHAT', enabled: next })}
          />

          <SettingRow
            icon="mic"
            title="Let people talk"
            help="Off ends the conversation and stops new ones"
            checked={view.allowVoice}
            onChange={(next) => onHostAction({ action: 'SET_ALLOW_VOICE', enabled: next })}
          />

          {/*
            Yours, not the room's.

            Everything above this line is something the host decides FOR the
            table; this one only changes what the person holding this phone
            hears. It is last for that reason, and it is here at all because it
            used to be a pill in the header of the screen before this one,
            beside a logo, in the middle of being asked your name.
          */}
          <SoundSetting />
        </div>
      ) : null}

      {/*
        The way on and the way out, pinned where a thumb is.

        A lobby had no way back at all: the only exits were the browser's own
        Back button and starting a game you had decided not to play.
      */}
      <ActionBar>
        <BackButton label={view.isHost ? 'Your games' : 'Leave'} onClick={onLeave} />
        {startButton}
      </ActionBar>
    </div>
  );
}
